import type { ReadmeEditor, Props } from '../types';

import React, { useCallback, useEffect, useState, useRef } from 'react';

import dispatch from '@core/utils/nativeDispatch';

import type Modal from '@ui/Modal';

import DashEditorNoProxy from './NoProxy';

export type DashEditorProps = JSX.IntrinsicAttributes &
  Omit<Props, 'doc' | 'onChange' | 'onInit'> & {
    doc?: { dirty?: boolean; value: string };
    html?: string;
    htmlMode?: boolean;
    onChange?: (proxyValue: EditorValueProxy) => void;
    onHtmlChange?: (html: string) => void;
    onInit?: (proxyValue: EditorValueProxy) => void;
    onRawChange?: (value: string) => void;
    onRawModeChange?: (rawMode: boolean) => void;
    rawMode?: boolean;
    setRawMode?: (rawMode: boolean) => void;
  };

/*
 * The typical way to serialize the MarkdownEditor is to grab the `editor`
 * object during `onInit` or `onChange` and call `editor.toString()`. However,
 * the `DashEditor` also contains raw mode, which doesn't have an editor
 * object. So we provide this proxy value which presents a similar interface.
 */
export interface EditorValueProxy {
  dirty: boolean;
  toJSON: () => string;
  toString: () => string;
}

/*
 * @todo: Remove Angular, then we can remove/refactor the event
 * emitters/listeners.
 */
export const DashEditor = ({
  doc = { value: '' },
  html,
  htmlMode,
  rawMode,
  onChange,
  onInit,
  onRawChange,
  ...props
}: DashEditorProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const modal = useRef<Modal>(null);
  const [md, setMd] = useState(doc.value);
  const [oldRawMode, setOldRawMode] = useState(rawMode);
  // @note: editor is mutable, it should always have the most recent changes,
  // so we only have to set it on init
  const [editor, setEditor] = useState<ReadmeEditor | null>(null);

  const proxyValue = useRef<EditorValueProxy>({
    ...doc,
    toJSON: () => doc.value,
    toString: () => doc.value,
    dirty: false,
  });

  const setDirty = useCallback(() => {
    proxyValue.current.dirty = true;
    dispatch({
      target: ref.current,
      value: { type: 'editor:dirty' },
    });
  }, []);

  const editorOnChange = useCallback(() => {
    setDirty();
    onChange?.(proxyValue.current);
  }, [onChange, setDirty]);

  const rawOnChange = useCallback(
    value => {
      setDirty();
      setMd(value);
      onChange?.(proxyValue.current);
      onRawChange?.(value);
    },
    [onChange, onRawChange, setDirty],
  );

  // We have to update the editor proxy value during:
  // - editor onInit
  // - raw mode onInit
  // - raw mode onChange
  useEffect(() => {
    proxyValue.current.toJSON = () => (rawMode ? md : editor?.toString() || '');
    proxyValue.current.toString = () => proxyValue.current.toJSON();
  }, [editor, md, rawMode]);

  // When we switch between raw and markdown mode, we update the proxyValue.
  // Perhaps it's a little counter-intuitive to call onInit at the same time as
  // onChange, but it means a parent component can subscribe to only
  // `onChange`.
  useEffect(() => {
    onInit?.(proxyValue.current);
    onChange?.(proxyValue.current);
    dispatch({
      target: ref.current,
      value: { type: 'editor:init', getValue: () => proxyValue.current.toString() },
    });
  }, [onChange, onInit]);

  // Reset dirty if we update the doc. We don't want to fully reset the
  // MarkdownEditor, because we want to avoid normalizing in progress work.
  useEffect(() => {
    setMd(doc.value);
    proxyValue.current.dirty = doc.dirty ?? false;
  }, [doc]);

  useEffect(() => {
    if (rawMode === oldRawMode) return;
    setOldRawMode(rawMode);

    /*
     * When we switch from rich to raw mode, we need to serialize the editor's
     * state into markdown.
     */
    if (rawMode) {
      setMd(editor?.toString() || '');
    }
  }, [editor, oldRawMode, rawMode]);

  return (
    <DashEditorNoProxy
      {...props}
      ref={ref}
      doc={md}
      editorOnChange={editorOnChange}
      html={html}
      htmlMode={htmlMode}
      modal={modal.current}
      onInitEditor={setEditor}
      rawMode={rawMode}
      rawOnChange={rawOnChange}
    />
  );
};

export default DashEditor;
